/**
 * html-to-markdown.js — serialize the small HTML subset that <g-notes> edits
 * back into the markdown it stores. It is the inverse of ./markdown.js and
 * covers exactly the same closed set of constructs:
 *
 *   <h1..h6>      → #..######
 *   <strong>/<b>  → **bold**
 *   <em>/<i>      → *italic*
 *   <del>/<s>     → ~~strikethrough~~
 *   <code>        → `inline code`   (or ```fenced``` inside <pre>)
 *   <ul>/<ol><li> → - / 1. lists    (one level of nesting, indented)
 *   <li><input>   → - [ ] / - [x] task items
 *   <blockquote>  → > quote
 *   <a href>      → [label](href)
 *   <img>         → ![alt](src)
 *   <table>       → | GFM | pipe | table |
 *   <hr>          → ---
 *   <br>          → line break
 *   <p>/<div>     → paragraph break
 *
 * Keeping this list equal to ./markdown.js's is not tidiness, it is the
 * contract: <g-notes> seeds a contenteditable with that renderer's HTML and
 * reads it straight back through here on save. A construct rendered but not
 * read is a construct that silently dies the first time someone opens the note
 * to fix a typo.
 *
 * Browser-only (uses the DOM to parse): <g-notes> edits in a contenteditable,
 * so this always runs in a document context. Class names are ignored — we key
 * off tag names only — so the class-decorated HTML that markdown.js emits and
 * the bare HTML that execCommand produces both serialize identically.
 */

const BLOCK = /^(div|p|h[1-6]|ul|ol|blockquote|pre|table|hr)$/;

export function htmlToMarkdown(html) {
  if (typeof html !== 'string' || !html.trim()) return '';
  const root = document.createElement('div');
  root.innerHTML = html;
  return serializeChildren(root)
    .replace(/ /g, ' ')      // nbsp → space
    .replace(/[ \t]+\n/g, '\n')   // trailing spaces before a newline
    .replace(/\n{3,}/g, '\n\n')   // collapse big gaps
    .trim();
}

// Walk a node's children, grouping inline runs into paragraphs and emitting
// block elements as their own markdown blocks (separated by a blank line).
function serializeChildren(node) {
  const out = [];
  let buf = '';
  const flush = () => { const t = buf.trim(); if (t) out.push(t); buf = ''; };
  node.childNodes.forEach((ch) => {
    if (ch.nodeType === 3) { buf += ch.nodeValue; return; }
    if (ch.nodeType !== 1) return;
    const tag = ch.tagName.toLowerCase();
    if (BLOCK.test(tag)) { flush(); out.push(serializeBlock(ch, tag)); }
    else if (tag === 'br') { buf += '\n'; }
    else { buf += inlineEl(ch); }
  });
  flush();
  return out.filter(Boolean).join('\n\n');
}

function serializeBlock(el, tag) {
  if (/^h[1-6]$/.test(tag)) return '#'.repeat(+tag[1]) + ' ' + inlineContent(el).trim();
  if (tag === 'ul' || tag === 'ol') return listMd(el, tag === 'ol');
  if (tag === 'hr') return '---';
  if (tag === 'table') return tableMd(el);
  if (tag === 'blockquote') {
    return inlineContent(el).trim().split('\n').map((l) => '> ' + l).join('\n');
  }
  if (tag === 'pre') return '```\n' + el.textContent.replace(/\n+$/, '') + '\n```';
  // <p> / <div>: recurse if it wraps further block elements, else a paragraph.
  if ([...el.children].some((c) => BLOCK.test(c.tagName.toLowerCase()))) return serializeChildren(el);
  return inlineContent(el).trim();
}

function listMd(listEl, ordered) {
  const items = [...listEl.children].filter((c) => c.tagName.toLowerCase() === 'li');
  return items.map((li, i) => {
    const box = li.querySelector(':scope > input[type="checkbox"]');
    const marker = box ? `- [${box.checked ? 'x' : ' '}] ` : (ordered ? `${i + 1}. ` : '- ');
    let text = '';
    const nested = [];
    li.childNodes.forEach((ch) => {
      if (ch.nodeType === 3) { text += ch.nodeValue; return; }
      if (ch.nodeType !== 1) return;
      const tag = ch.tagName.toLowerCase();
      if (tag === 'ul' || tag === 'ol') nested.push(listMd(ch, tag === 'ol'));
      else if (tag === 'br') text += ' ';
      else if (ch === box) return; // the box is the marker, not content
      else text += inlineEl(ch);
    });
    let line = marker + text.trim();
    if (nested.length) {
      line += '\n' + nested.join('\n').split('\n').map((l) => '  ' + l).join('\n');
    }
    return line;
  }).join('\n');
}

/**
 * A GFM pipe table. The first <tr> is the header — from <thead> if there is
 * one, otherwise the first row, because a contenteditable that has been edited
 * (or a table pasted in from elsewhere) will not reliably have kept its
 * <thead>. Column alignment is read off the cells' text-left/center/right
 * classes or an inline text-align, which is what markdown.js emits for it.
 */
function tableMd(tableEl) {
  const rows = [...tableEl.querySelectorAll('tr')]
    .map((tr) => [...tr.children].filter((c) => /^(td|th)$/.test(c.tagName.toLowerCase())));
  if (!rows.length) return '';

  const width = Math.max(...rows.map((r) => r.length));
  if (!width) return '';

  const cell = (c) => (c ? inlineContent(c).replace(/\s*\n\s*/g, ' ').replace(/\|/g, '\\|').trim() : '');
  const line = (r) => '| ' + Array.from({ length: width }, (_, i) => cell(r[i])).join(' | ') + ' |';

  const [head, ...body] = rows;
  const delim = '| ' + Array.from({ length: width }, (_, i) => {
    switch (alignOf(head[i])) {
      case 'center': return ':-:';
      case 'right': return '--:';
      case 'left': return ':--';
      default: return '---';
    }
  }).join(' | ') + ' |';

  return [line(head), delim, ...body.map(line)].join('\n');
}

function alignOf(cellEl) {
  if (!cellEl) return null;
  for (const a of ['left', 'center', 'right']) {
    if (cellEl.classList.contains(`text-${a}`)) return a;
  }
  const inline = (cellEl.style && cellEl.style.textAlign) || '';
  return ['left', 'center', 'right'].includes(inline) ? inline : null;
}

function inlineEl(el) {
  const tag = el.tagName.toLowerCase();
  if (tag === 'br') return '\n';
  if (tag === 'strong' || tag === 'b') return wrap('**', inlineContent(el));
  if (tag === 'em' || tag === 'i') return wrap('*', inlineContent(el));
  if (tag === 'del' || tag === 's' || tag === 'strike') return wrap('~~', inlineContent(el));
  if (tag === 'code') return '`' + el.textContent + '`';
  if (tag === 'img') {
    const src = (el.getAttribute('src') || '').trim();
    return src ? `![${(el.getAttribute('alt') || '').trim()}](${src})` : '';
  }
  if (tag === 'input') return ''; // a stray checkbox outside a list item
  if (tag === 'a') {
    const href = (el.getAttribute('href') || '').trim();
    const label = inlineContent(el).trim();
    return href ? `[${label}](${href})` : label;
  }
  return inlineContent(el); // span/font/other wrappers — unwrap
}

function inlineContent(el) {
  let out = '';
  el.childNodes.forEach((ch) => {
    if (ch.nodeType === 3) out += ch.nodeValue;
    else if (ch.nodeType === 1) out += inlineEl(ch);
  });
  return out;
}

// Wrap with a delimiter, keeping any leading/trailing whitespace OUTSIDE the
// markers (so `<b> x </b>` → ` **x** ` rather than the invalid `** x **`).
function wrap(delim, text) {
  if (!text || !text.trim()) return text || '';
  const lead = text.match(/^\s*/)[0];
  const trail = text.match(/\s*$/)[0];
  return lead + delim + text.trim() + delim + trail;
}
